import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Loading from '../../components/ui/Loading';
import api from 'utils/api';
import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import ClientHistoryModal from '../../components/modals/ClientHistoryModal';
import { toast } from 'react-hot-toast';

dayjs.extend(jalaliday);

const GlobalClients = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals State
  const [selectedClient, setSelectedClient] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Edit Form State
  const [editForm, setEditForm] = useState({
    name: '',
    nationalId: '',
    phoneNumber: '',
  });

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/clients/all');
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredClients(clients);
    } else {
      const lowerTerm = searchTerm.toLowerCase();
      const filtered = clients.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(lowerTerm)) ||
          (c.phoneNumber && c.phoneNumber.includes(lowerTerm)) ||
          (c.nationalId && c.nationalId.includes(lowerTerm))
      );
      setFilteredClients(filtered);
    }
  }, [searchTerm, clients]);

  // --- Handlers ---

  const handleOpenHistory = (client) => {
    setSelectedClient(client);
    setShowHistoryModal(true);
  };

  const handleOpenEdit = (client) => {
    setSelectedClient(client);
    setEditForm({
      name: client.name || '',
      nationalId: client.nationalId || '',
      phoneNumber: client.phoneNumber || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/api/clients/${selectedClient._id}`, editForm);
      toast.success('اطلاعات مشتری بروز شد');
      setShowEditModal(false);
      fetchClients(); // Refresh list
    } catch (error) {
      toast.error('خطا در ویرایش');
    }
  };

  const handleDeleteClient = async (client) => {
    if (
      window.confirm(
        `آیا از حذف مشتری "${
          client.name || client.phoneNumber
        }" مطمئن هستید؟ این کار غیرقابل بازگشت است.`
      )
    ) {
      try {
        await api.delete(`/api/clients/${client._id}`);
        toast.success('مشتری حذف شد');
        fetchClients();
      } catch (error) {
        toast.error('خطا در حذف');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="lg:mr-60 pt-16">
        <div className="p-6 space-y-6">
          <BreadcrumbNavigation />

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Icon name="Users" className="text-primary" />
                لیست جامع مشتریان
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                مدیریت تمام کاربران ثبت‌نام شده
              </p>
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg border border-border">
            <Input
              placeholder="جستجو (نام، موبایل، کد ملی)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-10 flex justify-center">
                <Loading />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-right text-sm font-medium">
                        نام مشتری
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium">
                        شماره موبایل
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium">
                        کد ملی
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium">
                        تاریخ عضویت
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium">
                        عملیات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredClients.map((client) => (
                      <tr
                        key={client._id}
                        className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-4 font-medium">
                          {client.name || (
                            <span className="text-muted-foreground text-xs">
                              (نامشخص)
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 dir-ltr text-right font-mono">
                          {client.phoneNumber}
                        </td>
                        <td className="px-6 py-4 dir-ltr text-right font-mono">
                          {client.nationalId || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {dayjs(client.createdAt)
                            .calendar('jalali')
                            .format('YYYY/MM/DD')}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="سوابق"
                              onClick={() => handleOpenHistory(client)}>
                              <Icon name="Eye" size={18} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="ویرایش"
                              onClick={() => handleOpenEdit(client)}>
                              <Icon
                                name="Edit"
                                size={18}
                                className="text-blue-600"
                              />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="حذف"
                              onClick={() => handleDeleteClient(client)}>
                              <Icon
                                name="Trash2"
                                size={18}
                                className="text-red-600"
                              />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* History Modal */}
      <ClientHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        client={selectedClient}
      />

      {/* Edit Modal (Inline Simple Modal) */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-modal flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-md rounded-lg shadow-xl p-6 space-y-4">
            <h3 className="text-lg font-bold">ویرایش مشتری</h3>
            <Input
              label="نام و نام خانوادگی"
              value={editForm.name}
              onChange={(e) =>
                setEditForm({ ...editForm, name: e.target.value })
              }
            />
            <Input
              label="شماره موبایل"
              value={editForm.phoneNumber}
              onChange={(e) =>
                setEditForm({ ...editForm, phoneNumber: e.target.value })
              }
              dir="ltr"
            />
            <Input
              label="کد ملی"
              value={editForm.nationalId}
              onChange={(e) =>
                setEditForm({ ...editForm, nationalId: e.target.value })
              }
              dir="ltr"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                انصراف
              </Button>
              <Button variant="default" onClick={handleSaveEdit}>
                ذخیره
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalClients;
